import { PlusIcon } from "@heroicons/react/24/outline";
import useAxios from "axios-hooks";
import { useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import BusinessUserForm from "./BusinessUserForm";
import AutocompleteInput from "../../../components/input/AutoCompleteInput";
import { UserResDto, UsersResDto } from "../../../api/Api";
import ConfirmationButton from "../../../components/button/ConfirmationButton";

import moment from "moment";
import PaginationButtons from "../../../components/pagination/PaginationButtons";

export function BusinessUsersTable({
  data,
  onRowClick,
}: {
  data: UserResDto[] | undefined;
  onRowClick: (user: UserResDto) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-xl table-container border border-base-300 bg-base-100">
      <table className="table table-md  w-full">
        <thead className="bg-base-200 sticky top-0 z-10 text-sm">
          <tr>
            <th>#</th>
            <th>User</th>
            <th>Status</th>
            <th>Role</th>
            <th>Classrooms</th>
            <th>Joined</th>
          </tr>
        </thead>
        <tbody>
          {data?.map((member, index) => (
            <tr
              key={member._id}
              className="hover:bg-base-300 cursor-pointer transition"
              onClick={() => onRowClick(member)}
            >
              <td>{index + 1}</td>

              <td>
                <div className="text-sm leading-tight">
                  <div className="font-medium">{member.username}</div>
                  <div className="text-xs text-base-content/70">
                    {member.email}
                  </div>
                </div>
              </td>

              <td>
                {member.enabled ? (
                  <div className="badge badge-success badge-sm">Active</div>
                ) : (
                  <div className="badge badge-error badge-sm">Disabled</div>
                )}
              </td>

              <td>
                <div className="badge badge-outline badge-sm capitalize">
                  {member.group.replace("BUSINESS_", "").toLowerCase()}
                </div>
              </td>

              <td>
                <div className="badge badge-info badge-sm">
                  {member.classrooms?.length || 0}
                </div>
              </td>

              <td className="text-xs text-base-content/60">
                {moment(member.createdAt).fromNow()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BusinessUsers(props: {
  filter?: {
    roles?: string[];
  };
}) {
  const params = useParams();
  const [searchString, setSearchString] = useState(undefined as any);
  const [clickedMember, setClickedMember] = useState(undefined as any);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [{ data, loading, error }, getMembers] = useAxios<UsersResDto>({
    url: `/businesses/${params.businessId ? params.businessId : "me"}/users`,
    params: {
      search: searchString,
      roles: props.filter?.roles,
      page,
      limit: pageSize,
    },
  });

  const contenxt = useOutletContext() as any;

  useEffect(() => {
    contenxt?.setTitleComponent(
      <div className="flex items-center justify-center gap-2">
        Organization Users
      </div>
    );
  }, []);

  useEffect(() => {
    if (searchString !== undefined) {
      getMembers();
    }
  }, [searchString]);

  return (
    <div className="flex flex-col gap-2 p-5">
      <div className="flex">
        <div className="mb-2">
          <AutocompleteInput
            onComplete={(input: string) => {
              setSearchString(input);
            }}
            placeHolder="Search..."
            className="bg-base-200"
          ></AutocompleteInput>
        </div>
        <div className="ml-auto">
          <button
            className="btn btn-sm"
            onClick={() => {
              (
                document?.getElementById("new_member_modal") as any
              )?.showModal();
            }}
          >
            <PlusIcon className="h-4"></PlusIcon>
            New Organization User
          </button>
        </div>
      </div>
      {!loading ? (
        <div className="flex gap-4 flex-col">
          <BusinessUsersTable
            onRowClick={(user: any) => {
              setClickedMember(user);
              (
                document?.getElementById("update_member_modal") as any
              )?.showModal();
            }}
            data={data?.docs}
          ></BusinessUsersTable>
          <PaginationButtons
            page={page}
            setPage={setPage}
            pageCount={data?.totalPages || 1}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </div>
      ) : (
        <div className="h-[20vh] w-full flex items-center justify-center">
          <span className="loading loading-spinner loading-md"></span>
        </div>
      )}

      <dialog id="new_member_modal" className="modal">
        <div className="modal-box">
          <h3 className="font-bold text-lg pb-2">Create Organization User</h3>
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">
              ✕
            </button>
          </form>
          <div>
            <BusinessUserForm
              // defaultValues={clickedMember}
              onSuccess={(user: UserResDto) => {
                // navigate(`${user?._id}/stats`);
                getMembers();
                (document?.getElementById("new_member_modal") as any)?.close();
              }}
            ></BusinessUserForm>
          </div>
          {/* <div className="alert text-xs italic bg-transparent bg-base-100 border-0 opacity-50 p-0 mt-4">
            <InformationCircleIcon className="h-6"></InformationCircleIcon>
            You will be charged automatically for the new Member and an
            invitaion email with a temporary password will be sent to the member
            that you add.
          </div> */}
        </div>
      </dialog>

      <dialog id="update_member_modal" className="modal">
        <div className="modal-box">
          <h3 className="font-bold text-lg pb-2">Update Organization User</h3>
           <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">
              ✕
            </button>
          </form>
          <div>
            {clickedMember && (
              <UpdateBusinessUser
                businessId={params.businessId}
                user={clickedMember}
                onFinish={() => {
                  getMembers();
                  (
                    document?.getElementById("update_member_modal") as any
                  )?.close();
                }}
              ></UpdateBusinessUser>
            )}
          </div>
          {/* <div className="alert text-xs italic bg-transparent bg-base-100 border-0 opacity-50 p-0 mt-4">
            <InformationCircleIcon className="h-6"></InformationCircleIcon>
            You will be charged automatically for the new Member and an
            invitaion email with a temporary password will be sent to the member
            that you add.
          </div> */}
        </div>
      </dialog>
    </div>
  );
}

export function UpdateBusinessUser(props: {
  businessId?: string;
  user: any;
  onFinish: any;
}) {
  const [role, setRole] = useState();

  const [
    {
      data: updateMemberData,
      loading: updateMemberLoading,
      error: updateMemberError,
    },
    updateMember,
  ] = useAxios<any, any>(
    {
      url: `/businesses/${props?.businessId}/users/${props?.user?.userId}`,
    },
    {
      manual: true,
    }
  );

  if (updateMemberLoading) {
    return <div>...</div>;
  }

  return (
    <div className="flex flex-col gap-10">
      <div className="form-control w-full">
        <div className="label">
          <span className="label-text">Role</span>
        </div>
        <select
          // defaultValue={props?.defaultValues?.group || ""}
          className="select select-bordered select-sm w-full"
          onChange={(e) => {
            setRole(e?.target?.value as any);
          }}
        >
          <option value="" disabled>
            Select a role
          </option>
          <option
            value="BUSINESS_ADMIN"
            selected={props.user?.group === "BUSINESS_ADMIN"}
          >
            Organization Admin
          </option>
          <option
            value="BUSINESS_INSTRUCTOR"
            selected={props.user?.group === "BUSINESS_INSTRUCTOR"}
          >
            Organization Instructor
          </option>
          {/* <option
            value="BUSINESS_MEMBER"
            selected={props.user?.group === "BUSINESS_MEMBER"}
          >
            Organization Member/Student
          </option> */}
        </select>
      </div>
      <div className="flex">
        <button
          onClick={async () => {
            try {
              await updateMember({ method: "PATCH", data: { role: role } });
              props.onFinish();
            } catch (error) {}
          }}
          className="btn btn-sm  btn-primary"
          type="submit"
        >
          SAVE CHANGES
        </button>
        <ConfirmationButton
          id={"conf_button"}
          buttonText={"DELETE USER"}
          body={"Are you sure you want to delete user"}
          title={"Delete User"}
          onAccept={async () => {
            try {
              await updateMember({
                method: "DELETE",
              });
              props.onFinish();
            } catch (error) {}
          }}
          onReject={() => {}}
          className={"btn btn-error btn-sm ml-auto"}
        ></ConfirmationButton>
      </div>
    </div>
  );
}
